import type { ReactNode } from "react"
import Modal from "@/components/Modal/Modal"
import TextField from "@/components/TextField/TextField"
import {
  ABOUT_LABEL,
  ABOUT_PLACEHOLDER,
  CUSTOMER_BLURB,
  SIZE_OPTIONS,
  SIZE_PLACEHOLDER,
} from "@/content/customers"
import styles from "@/styles/components/CustomerModal.module.css"

export type CustomerField = "name" | "sector" | "size" | "about"

interface CustomerFieldBinding {
  value: string
  onChange: (event: { target: { value: string } }) => void
  onBlur?: () => void
}

interface CustomerModalProps {
  mode: "create" | "edit"
  binding: (field: CustomerField) => CustomerFieldBinding
  onClose: () => void
  actions: ReactNode
  error?: string | null
}

export default function CustomerModal({
  mode,
  binding,
  onClose,
  actions,
  error,
}: CustomerModalProps) {
  const creating = mode === "create"

  return (
    <Modal
      title={creating ? "New customer" : "Customer details"}
      description={CUSTOMER_BLURB}
      icon={<CustomerGlyph />}
      width={creating ? "default" : "narrow"}
      dismissible
      onClose={onClose}
      actions={actions}
    >
      <TextField
        label={creating ? "Company Name" : "Company"}
        placeholder="Acme Corp"
        required={creating}
        strong
        {...binding("name")}
      />
      <div className={styles.row}>
        <TextField label="Sector" placeholder="Logistics" {...binding("sector")} />
        <TextField
          kind="select"
          label="Company Size"
          options={SIZE_OPTIONS}
          placeholder={SIZE_PLACEHOLDER}
          {...binding("size")}
        />
      </div>
      <TextField
        kind="multiline"
        label={ABOUT_LABEL}
        placeholder={ABOUT_PLACEHOLDER}
        {...binding("about")}
      />
      {error ? <p className={styles.error}>{error}</p> : null}
    </Modal>
  )
}

/** Two stacked sheets: one customer, many sessions under it. */
function CustomerGlyph() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="7" y="3" width="10" height="12" rx="2" />
      <path d="M13 17H5a2 2 0 0 1-2-2V7" />
    </svg>
  )
}
